import { useEffect, useState } from "react";
import { Loader2, UserX } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** Chauffeur bills a no-show — only unlocks once they have waited out the operator's window. */
export const NoShowButton = ({ trip, onDone, onError }) => {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/trips/${trip.id}/no-show`);
        if (!cancelled) setState(data);
      } catch {
        if (!cancelled) setState(null);
      }
    };
    load();
    const timer = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [trip.id]);

  if (!state || !state.policy?.enabled) return null;

  if (!state.can_bill) {
    return (
      <p data-testid={`no-show-wait-${trip.number}`} className="text-center text-[11px] text-slate-500">
        {state.reason}
      </p>
    );
  }

  if (!open) {
    return (
      <Button variant="outline" data-testid={`no-show-open-${trip.number}`}
        onClick={() => setOpen(true)}
        className="h-12 w-full border-amber-400/40 bg-transparent text-amber-200 hover:bg-amber-400/10">
        <UserX className="mr-2 h-4 w-4" /> Passenger never showed
      </Button>
    );
  }

  return (
    <div data-testid={`no-show-form-${trip.number}`} className="space-y-3 rounded-lg border border-amber-400/40 bg-amber-400/5 p-4">
      <p className="text-sm text-slate-100">
        Close trip #{trip.number} as a no-show after {state.waited_minutes} min on scene?
      </p>
      <p data-testid={`no-show-amount-${trip.number}`} className="font-mono text-xs text-amber-200">
        Charging {money(state.total_fee)}
        {state.wait_charge > 0 && ` (${money(state.fee)} fee + ${money(state.wait_charge)} waiting)`}
      </p>
      <Input data-testid={`no-show-note-${trip.number}`} value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="What happened? (optional)"
        className="h-11 border-ink-600 bg-ink-800 text-slate-100" />
      <div className="flex gap-2">
        <Button data-testid={`no-show-confirm-${trip.number}`} disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await api.post(`/trips/${trip.id}/no-show`, { note });
              setOpen(false);
              onDone?.();
            } catch (e) { onError?.(errMsg(e)); } finally { setBusy(false); }
          }}
          className="bg-amber-400/20 text-amber-100 hover:bg-amber-400/30">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm no-show"}
        </Button>
        <Button variant="ghost" className="text-slate-400" onClick={() => setOpen(false)}>
          Keep waiting
        </Button>
      </div>
    </div>
  );
};
